import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-30px); }
  to { opacity: 1; transform: translateX(0); }
`;

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 1.5rem;
`;

const HeroSection = styled.section`
  text-align: center;
  margin-bottom: 4rem;
  animation: ${fadeIn} 0.8s ease-out;
`;

const Title = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1.5rem;
  color: #000;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  max-width: 800px;
  margin: 0 auto 3rem;
  line-height: 1.6;
  color: #000;
`;

const SectionTitle = styled.h2`
  font-size: 2.6rem;
  margin-bottom: 2rem;
  color: #000;
  text-align: center;
`;

const TeamSection = styled.section`
  margin-bottom: 5rem;
  animation: ${fadeIn} 0.6s ease-out;
`;

const LeadershipGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2.5rem;
  margin-bottom: 5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const LeaderCard = styled.div`
  display: flex;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const LeaderImage = styled.div`
  width: 180px;
  background-color: #f5f5f5;
  background-image: ${props => props.image ? `url(${props.image})` : 'none'};
  background-size: cover;
  background-position: center;
  
  @media (max-width: 576px) {
    width: 100%;
    height: 200px;
  }
`;

const LeaderInfo = styled.div`
  padding: 1.5rem;
  flex: 1;
`;

const LeaderName = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 0.3rem;
  color: #000;
`;

const LeaderTitle = styled.p`
  font-size: 1rem;
  color: #000;
  margin-bottom: 1rem;
  font-weight: 500;
`;

const LeaderBio = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: #000;
`;

const StandardsSection = styled.section`
  margin-bottom: 5rem;
  animation: ${fadeIn} 0.8s ease-out;
`;

const StandardsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StandardsCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2.5rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
`;

const StandardsTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  color: #000;
`;

const StandardsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const StandardsItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  font-size: 1rem;
  color: #000;
  animation: ${slideIn} ${props => 0.3 + props.index * 0.1}s ease-out;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const CheckIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-right: 12px;
  color: #000;
`;

const JoinSection = styled.section`
  text-align: center;
  padding: 4rem 2rem;
  background: ${props => props.theme?.colors?.lightAccent || '#f5f9ff'};
  border-radius: 12px;
  margin-bottom: 2rem;
  animation: ${fadeIn} 1s ease-out;
`;

const JoinText = styled.p`
  font-size: 1.2rem;
  max-width: 800px;
  margin: 0 auto 2rem;
  line-height: 1.6;
  color: #000;
`;

const Button = styled(Link)`
  display: inline-block;
  background: ${props => props.theme?.colors?.primary || '#4a90e2'};
  color: white;
  border: none;
  border-radius: 50px;
  padding: 1rem 2.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme?.colors?.accent || '#2a70c2'};
    transform: translateY(-3px);
  }
`;

// Mock data for leadership team
const leadershipTeam = [
  {
    id: 1,
    name: "Dr. Maria Rodriguez",
    title: "Chief Nutrition Officer",
    bio: "Dr. Rodriguez has over 15 years of experience in clinical nutrition and leads our team of expert nutritionists. She specializes in metabolic health and nutrition science.",
    image: null
  },
  {
    id: 2,
    name: "Dr. James Wilson",
    title: "Medical Director",
    bio: "Board-certified in endocrinology, Dr. Wilson ensures all our nutrition protocols meet the highest standards of medical accuracy and effectiveness.",
    image: null
  },
  {
    id: 3,
    name: "Sarah Chen, PhD",
    title: "Research Director",
    bio: "Sarah leads our research initiatives, staying at the forefront of nutrition science and translating the latest findings into practical advice for our clients.",
    image: null
  },
  {
    id: 4,
    name: "Michael Johnson, RD",
    title: "Head of Nutritionist Training",
    bio: "Michael ensures all GeoDiet nutritionists receive comprehensive training in our unique methodology and stay updated with continuing education.",
    image: null
  }
];

// Standards data
const professionalCredentials = [
  "Registered Dietitian Nutritionist (RDN)",
  "Certified Nutrition Specialist (CNS)",
  "Certified Clinical Nutritionist (CCN)",
  "Board Certified in Holistic Nutrition",
  "Certified Sports Nutritionist",
  "Certified Diabetes Educator (CDE)"
];

const selectionCriteria = [
  "Minimum of a master's degree in nutrition or related field",
  "At least 3 years of clinical experience",
  "Ongoing professional development and education",
  "Adherence to evidence-based practice guidelines",
  "Regular case reviews and quality assessment",
  "Commitment to personalized client care"
];

const OurExperts = () => {
  return (
    <PageContainer>
      <HeroSection>
        <Title>Om Wonder teknologin</Title>
        <Subtitle>
          Meet the team that leads our network of highly qualified nutritionists, committed
          to transforming health through personalized nutrition
        </Subtitle>
      </HeroSection>

      <TeamSection>
        <SectionTitle>Leadership Team</SectionTitle>
        <LeadershipGrid>
          {leadershipTeam.map(leader => (
            <LeaderCard key={leader.id}>
              <LeaderImage image={leader.image} />
              <LeaderInfo>
                <LeaderName>{leader.name}</LeaderName>
                <LeaderTitle>{leader.title}</LeaderTitle>
                <LeaderBio>{leader.bio}</LeaderBio>
              </LeaderInfo>
            </LeaderCard>
          ))}
        </LeadershipGrid>
      </TeamSection>

      <StandardsSection>
        <SectionTitle>Our Quality Standards</SectionTitle>
        <StandardsContainer>
          <StandardsCard>
            <StandardsTitle>Professional Credentials</StandardsTitle>
            <StandardsList>
              {professionalCredentials.map((credential, index) => (
                <StandardsItem key={index} index={index}>
                  <CheckIcon>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13.3334 4L6.00008 11.3333L2.66675 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </CheckIcon>
                  {credential}
                </StandardsItem>
              ))}
            </StandardsList>
          </StandardsCard>
          
          <StandardsCard>
            <StandardsTitle>Selection Criteria</StandardsTitle>
            <StandardsList>
              {selectionCriteria.map((criterion, index) => (
                <StandardsItem key={index} index={index}>
                  <CheckIcon>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13.3334 4L6.00008 11.3333L2.66675 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </CheckIcon>
                  {criterion}
                </StandardsItem>
              ))}
            </StandardsList>
          </StandardsCard>
        </StandardsContainer>
      </StandardsSection>

      <JoinSection>
        <SectionTitle>Join Our Network</SectionTitle>
        <JoinText>
          Are you a qualified nutritionist interested in joining our platform? We're always
          looking for talented professionals to join our team.
        </JoinText>
        <Button to="/become-nutritionist">Learn More</Button>
      </JoinSection>
    </PageContainer>
  );
};

export default OurExperts;